"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { useSiteAuthStore } from "@gerama/stores";
import { cn, formatNumber, formatTimeAgo } from "@gerama/utils";
import type { InboxNotification } from "@gerama/api/core";
import { browserCoreApi } from "@/lib/api";
import { MediaImage } from "@/components/media-image";
import { Popover } from "@/components/popover";

const KEY = ["notifications", "inbox"] as const;

/** Where an item opens — its deep link when the admin set one, else the content it announces. */
function hrefFor(item: InboxNotification): string | null {
  if (item.deepLink?.startsWith("/")) return item.deepLink;
  switch (item.refType?.toLowerCase()) {
    case "song":
      return item.refId ? `/songs/${item.refId}` : null;
    case "video":
      return item.refId ? `/videos/${item.refId}` : null;
    case "artist":
      return item.refId ? `/artists/${item.refId}` : null;
    case "podcast":
      return item.refId ? `/podcasts/${item.refId}` : null;
    case "episode":
      return item.refId ? `/podcasts/episodes/${item.refId}` : null;
    default:
      return null;
  }
}

/**
 * The header bell: new songs / videos / podcasts from what the listener follows
 * plus messages from the team (a discount code shows with a copy button). The
 * unread count refreshes every minute while signed in.
 */
export function NotificationBell() {
  const user = useSiteAuthStore((s) => s.user);
  const client = useQueryClient();
  const router = useRouter();
  const inbox = useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) => browserCoreApi.notifications.inbox({ limit: 20 }, signal),
    enabled: !!user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const markRead = useMutation({
    mutationFn: (id: number) => browserCoreApi.notifications.markRead(id),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });
  const markAll = useMutation({
    mutationFn: () => browserCoreApi.notifications.markAllRead(),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY }),
  });

  if (!user) return null;
  const unread = inbox.data?.unreadCount ?? 0;
  const items = inbox.data?.list.list ?? [];

  return (
    <Popover
      className="w-[22rem] max-w-[calc(100vw-1.5rem)]"
      trigger={({ toggle, open }) => (
        <button
          type="button"
          onClick={toggle}
          aria-label={unread ? `اعلان‌ها، ${unread} خوانده‌نشده` : "اعلان‌ها"}
          aria-expanded={open}
          className="text-muted-foreground hover:text-foreground relative p-2"
        >
          <Bell className="size-5" />
          {unread > 0 && (
            <span className="bg-primary text-primary-foreground absolute end-0.5 top-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-bold">
              {unread > 9 ? "۹+" : formatNumber(unread, "fa")}
            </span>
          )}
        </button>
      )}
    >
      {(close) => (
        <div className="flex max-h-[70vh] flex-col">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-sm font-bold">اعلان‌ها</h2>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                همه را خواندم
              </button>
            )}
          </div>
          <ul className="divide-border/60 min-h-0 flex-1 divide-y overflow-y-auto">
            {items.length === 0 && (
              <li className="text-muted-foreground px-4 py-10 text-center text-sm">
                {inbox.isPending ? "در حال بارگذاری…" : "اعلان تازه‌ای نداری"}
              </li>
            )}
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onOpen={() => {
                  if (!item.isRead) markRead.mutate(item.id);
                  const href = hrefFor(item);
                  if (href) {
                    close();
                    router.push(href);
                  }
                }}
              />
            ))}
          </ul>
          <Link
            href="/account"
            onClick={close}
            className="text-muted-foreground hover:text-foreground border-border/60 border-t px-4 py-2.5 text-center text-xs"
          >
            تنظیمات اعلان‌ها
          </Link>
        </div>
      )}
    </Popover>
  );
}

function NotificationRow({ item, onOpen }: { item: InboxNotification; onOpen: () => void }) {
  return (
    <li className={cn("hover:bg-accent/50 flex gap-3 px-4 py-3", !item.isRead && "bg-primary/5")}>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 gap-3 text-start">
        <span className="bg-muted relative size-11 shrink-0 overflow-hidden rounded-md">
          {item.image ? (
            <MediaImage src={item.image} alt="" fill sizes="44px" className="object-cover" />
          ) : (
            <Bell className="text-muted-foreground absolute inset-0 m-auto size-5" />
          )}
        </span>
        <span className="min-w-0 flex-1 space-y-0.5">
          <span className="flex items-center gap-2 text-sm font-semibold">
            <span className="truncate">{item.title}</span>
            {!item.isRead && <span className="bg-primary size-1.5 shrink-0 rounded-full" />}
          </span>
          {item.body && (
            <span className="text-muted-foreground line-clamp-2 block text-xs">{item.body}</span>
          )}
          <span className="text-muted-foreground/80 block text-[11px]">
            {formatTimeAgo(item.createdAt)}
          </span>
        </span>
      </button>
      {item.code && <CopyCode code={item.code} />}
    </li>
  );
}

/** The copyable code (discount code…) attached to a message. */
function CopyCode({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(code).then(
          () => {
            setCopied(true);
            toast.success("کد کپی شد");
            window.setTimeout(() => setCopied(false), 2000);
          },
          () => toast.error("کپی نشد"),
        );
      }}
      className="border-border hover:bg-accent flex h-8 shrink-0 items-center gap-1.5 self-center rounded-md border border-dashed px-2 font-mono text-xs"
      aria-label={`کپی کد ${code}`}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      <span dir="ltr">{code}</span>
    </button>
  );
}
